import React, {FC} from 'react';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontEvent} from '../../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {Dropdown, DropdownProps} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownBanner} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownBanner';
import {DropdownList} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownSeparator} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownSeparator';
import {DropdownItemContent} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {SimpleDropdownItem} from '../../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {tagHasBehaviors} from '../../../../../../helpers/models/tagHelpers';
import {TagBehaviorsEnum, TagModel} from '../../../../../../models/api/tagModel';
import {PermissionDropdownItem} from '../../../../../common/dropdowns/permissionDropdownItem';
import {PermissionSimpleDropdownItem} from '../../../../../common/dropdowns/permissionSimpleDropdownItem';

/*
 * Props.
 */

interface TagDropdownProps extends DropdownProps {
  tag: TagModel;
  isAddedToConversations: boolean;
  hasEditPermission: boolean;
  hasApplyPermission: boolean;
  hasVisibleCustomView: boolean;
  canPinTag: boolean;
  onNavigateToInboxClick: () => void;
  onViewConversationsClick: () => void;
  onEditClick: (event: FrontEvent) => void;
  onAddTagClick: (event: FrontEvent) => void;
  onUntagClick: (event: FrontEvent) => void;
  onShowClick: () => void;
  onHideClick: () => void;
}

/*
 * Component.
 */

export const TagDropdown: FC<TagDropdownProps> = (props) => {
  const {hasVisibleCustomView, tag, canPinTag} = props;
  const {isMainInboxTag, archivedAt} = tag;

  return (
    <Dropdown {...props} maxWidth={220}>
      <DropdownList {...props}>
        {archivedAt && (
          <DropdownBanner>
            <Trans id="tag-details-archived">This tag is archived</Trans>
          </DropdownBanner>
        )}
        {isMainInboxTag && (
          <SimpleDropdownItem onSelect={props.onNavigateToInboxClick}>
            <Trans id="tag-details-gotoinbox">Go to Inbox</Trans>
          </SimpleDropdownItem>
        )}
        {!isMainInboxTag && (
          // TODO: Pass permission prop for access check instead of getHasEditPermission selector.
          <PermissionDropdownItem onSelect={props.onEditClick} isDisabled={!props.hasEditPermission}>
            <DropdownItemContent>
              <Trans id="tag-details-edit">Edit</Trans>
            </DropdownItemContent>
          </PermissionDropdownItem>
        )}
        {renderAddRemoveItem(props)}
        {!isMainInboxTag && <DropdownSeparator />}
        {!isMainInboxTag && (
          <SimpleDropdownItem onSelect={props.onViewConversationsClick}>
            <Trans id="tag-details-viewconversations">View conversations</Trans>
          </SimpleDropdownItem>
        )}
        {canPinTag && !isMainInboxTag && hasVisibleCustomView && !archivedAt && (
          <PermissionSimpleDropdownItem onSelect={props.onHideClick}>
            <Trans id="tag-details-hideinsidebar">Hide in sidebar</Trans>
          </PermissionSimpleDropdownItem>
        )}
        {canPinTag && !isMainInboxTag && !hasVisibleCustomView && !archivedAt && (
          <PermissionSimpleDropdownItem onSelect={props.onShowClick}>
            <Trans id="tag-details-showinsidebar">Show in sidebar</Trans>
          </PermissionSimpleDropdownItem>
        )}
      </DropdownList>
    </Dropdown>
  );
};

function renderAddRemoveItem(props: TagDropdownProps) {
  const {tag, isAddedToConversations, hasApplyPermission, onAddTagClick, onUntagClick} = props;

  if (!tagHasBehaviors(tag, TagBehaviorsEnum.CAN_APPLY_FROM_LIST)) {
    return null;
  }

  if (isAddedToConversations) {
    return (
      <PermissionSimpleDropdownItem onSelect={onUntagClick} isDisabled={!hasApplyPermission}>
        <Trans id="tag-details-remove">Remove from conversation</Trans>
      </PermissionSimpleDropdownItem>
    );
  }

  return (
    <PermissionSimpleDropdownItem onSelect={onAddTagClick} isDisabled={!hasApplyPermission}>
      <Trans id="tag-details-add">Add to conversation</Trans>
    </PermissionSimpleDropdownItem>
  );
}
